import request from "./request";
const API_PREFIX = "/mp/order";

/**
 * 用户结束订单
 * @param {Object} data
 * @param {string|number} data.orderId - 订单ID（必填）
 * @param {number} [data.action]       - 操作类型：1 中途开门补差价，2 结束存包补差价（与 getOrderPriceDifference 一致）
 * @param {number} [data.totalAmount]  - 微信支付补差价金额，与 balance 互斥
 * @param {number} [data.balance]      - 余额支付补差价金额，与 totalAmount 互斥
 */
export function endOrder(data) {
  return request({
    url: `${API_PREFIX}/endOrder`,
    method: "post",
    data,
  });
}

/**
 * 获取计费配置（后端根据扫码会话自动关联柜子）
 * @returns {Promise<{ businessType, businessTypeId, configs: Array }>}
 */
export function getBillingConfig() {
  return request({
    url: `${API_PREFIX}/getBillConfig`,
    method: "get",
  });
}

/**
 * 续费：获取计费配置（无需扫码，按当前订单关联柜机）
 * @param {Object} params
 * @param {string|number} params.orderId - 待续费的订单主键 ID（与后端 Swagger 一致）
 * @returns {Promise<{ businessType?, businessTypeId?, configs?: Array }|object>} 结构与 getBillConfig 一致或为单条配置
 */
export function getBillConfigForRenew(params) {
  return request({
    url: `${API_PREFIX}/getBillConfigForRenew`,
    method: "get",
    params,
  });
}

/**
 * 续费：当前终端下可用的格口列表（更换柜口时使用，无需扫码）
 * @param {Object} params
 * @param {string|number} params.orderId    - 原订单主键 ID（必填，用于归属校验）
 * @param {string|number} params.terminalId - 终端/柜机 ID（必填）
 */
export function getBoxListForRenew(params) {
  return request({
    url: `${API_PREFIX}/getBoxListForRenew`,
    method: "get",
    params,
  });
}

/**
 * 是否需要扫码（后端根据当前用户 token 判断）
 * @returns {Promise<boolean|{shouldScan: boolean}>}
 */
export function shouldScan() {
  return request({
    url: `${API_PREFIX}/shouldScan`,
    method: "get",
  });
}

/**
 * 解析 shouldScan 接口返回值：是否仍需要扫码（与路由守卫逻辑一致）
 */
export function isNeedScan(scanResult) {
  return scanResult === true || (scanResult && scanResult.shouldScan === true);
}

/**
 * 创建订单
 * @param {Object} data
 * @param {number} data.storeBillingConfigId  - 计费配置 ID
 * @param {number} data.amount                - 订单金额（= prepaidPrice）
 * @param {number} data.deposit               - 本次需缴押金（账户押金已足够时传 0）
 * @param {number} [data.totalAmount]         - 微信支付时传（amount + deposit），与 balance 互斥
 * @param {number} [data.balance]             - 余额支付时传（amount + deposit），与 totalAmount 互斥
 * @param {number} data.boxId                 - 柜口 ID
 * @param {string} [data.orderNo]             - 续费场景下传原单业务单号（与 renew 关联的新单）
 */
export function createOrder(data) {
  return request({
    url: `${API_PREFIX}/createOrder`,
    method: "post",
    data,
  });
}

/**
 * 获取订单详情
 * @param {string|number} id - 订单 ID
 */
export function getOrderDetail(orderNo) {
  return request({
    url: `${API_PREFIX}/getOrderDetail`,
    method: "get",
    params: { orderNo },
  });
}

/**
 * 获取用户消费订单列表（寄存/消费类订单，一次性返回，不分页）
 * @param {Object} [params]
 * @param {string|null} [params.orderStatusEnum] - 订单状态枚举，传 `null` 或不传表示查询全部状态（默认 `null`）
 * @param {*} [params...] - 其它后端支持的 query 参数
 */
export function getOrderList(params = {}) {
  const { orderStatusEnum = null, ...rest } = params;
  return request({
    url: `${API_PREFIX}/getOrderList`,
    method: "get",
    params: {
      orderStatusEnum,
      ...rest,
    },
  });
}

/**
 * 获取用户提现订单列表（一次性返回，不分页）
 * @param {Object} [params] - 其它后端支持的 query 参数
 */
export function getWithdrawOrderList(params = {}) {
  return request({
    url: `${API_PREFIX}/getWithdrawalList`,
    method: "get",
    params,
  });
}

/**
 * 获取用户退款订单列表（一次性返回，不分页）
 * @param {Object} [params] - 其它后端支持的 query 参数
 */
export function getRefundOrderList(params = {}) {
  return request({
    url: `${API_PREFIX}/refundOrderList`,
    method: "get",
    params,
  });
}

/**
 * 获取当前进行中的订单
 */
export function getCurrentOrder(params = {}) {
  return request({
    url: `${API_PREFIX}/getCurrentOrder`,
    method: "get",
    params,
  });
}

/**
 * 存包入口的洗浴订单轻量检查，不加载完整订单详情。
 */
export function getStorageRouteContext() {
  return request({
    url: `${API_PREFIX}/getStorageRouteContext`,
    method: "get",
  });
}

/**
 * 获取订单价格差异
 * @param {Object} params - 参数对象，包括 orderId（订单ID）和 action（操作类型）
 * @param {string|number} params.orderId - 订单ID
 * @param {string|number} params.action - 操作类型
 */
export function getOrderPriceDifference(data) {
  return request({
    url: `${API_PREFIX}/getOrderPriceDifference`,
    method: "post",
    data,
  });
}

/**
 * 补缴差价：获取支付信息（微信调起参数或余额扣款结果）
 * @param {Object} data
 * @param {string} data.orderNo - 订单编号（必填）
 * @param {number} [data.totalAmount] - 微信支付补缴金额，与 balance 互斥
 * @param {number} [data.balance] - 余额支付补缴金额，与 totalAmount 互斥
 * @param {number} data.action - 1 中途开门，2 结束存包；服务端据此重算差价
 */
export function getPaymentInfo(data) {
  return request({
    url: `${API_PREFIX}/getPaymentInfo`,
    method: "post",
    data,
  });
}

/**
 * 用户支付待支付订单（Swagger：GET /mp/order/payOrder，query 仅 orderId）
 * @param {string|number} orderId - 订单 ID
 */
export function payOrder(orderId) {
  return request({
    url: `${API_PREFIX}/payOrder`,
    method: "get",
    params: { orderId },
  });
}

/**
 * 查询服务端是否已处理微信支付回调。
 * @param {string} orderNo - 业务订单号
 * @param {boolean} [completed=false] - 补缴场景需等待订单结算完成
 */
export function checkPayStatus(orderNo, completed = false) {
  return request({
    url: `${API_PREFIX}/checkPayStatus`,
    method: "get",
    params: { orderNo, completed },
  });
}
